/**
 * Шаги мастера онбординга. Чистая логика без React — чтобы легко тестировать
 * и вставлять условные шаги (например, «Ты на ARENA?») между существующими.
 * `order` пишется в profiles.onboarding_step после «Дальше»; между шагами
 * оставлены промежутки, чтобы новый шаг можно было вставить без перенумерации.
 */
import { isTelegramLike } from "@/lib/coffee-contact";
import { normalizeWhatsapp } from "@/lib/whatsapp";
import { hasSeekingText } from "@/lib/looking-for";

export const OPT_OUT_STATUS = "пока_не_готов";

export type StepId =
  /** Новый мастер: «Что ты хочешь делать в TurkeyDrive?» */
  | "intents"
  | "interests"
  | "city"
  | "photo"
  | "about"
  | "meet"
  | "contact"
  | "district"
  | "services"
  /** Только в режиме ARENA: отдельный шаг «кого ищешь». */
  | "purpose"
  /** Только в режиме ARENA: «Что ищешь» своими словами. */
  | "seeking";

export type StepKind = "required" | "optional";

export type OnboardingFacts = {
  interests: string[];
  minInterests: number;
  /** profiles.city; «Не указан» считается пустым. */
  city: string | null | undefined;
  profession: string;
  /** profiles.coffee_status */
  meetStatus: string[];
  /** Telegram или любая непустая social_link. */
  hasContact: boolean;
  /** Выбрано «Пока только изучаю». */
  optOut: boolean;
  hasDistrictList: boolean;
  /** profiles.visitor_kind === 'tourist' — «Я турист, города меняются». */
  tourist?: boolean;
  district: string | null | undefined;
  photoUrl: string | null | undefined;
  isExpert: boolean;
  /** profiles.onboarding_step — order последнего пройденного шага. */
  savedStep: number;
  /** profiles.about — нужен для режима ARENA. */
  about?: string | null;
  /** profiles.looking_for_tags — нужен для режима ARENA. */
  lookingForTags?: string[];
  /** profiles.looking_for — обязательный текст в режиме ARENA. */
  lookingFor?: string | null;
  /** Намерения из user_platform_intents. */
  intents?: string[];
};

export type StepDef = {
  id: StepId;
  order: number;
  kind: StepKind;
  /** Показывать ли шаг этому человеку вообще. */
  applies: (f: OnboardingFacts) => boolean;
  /** Данные уже есть (или действие уже выполнено). */
  done: (f: OnboardingFacts) => boolean;
};

const hasText = (v: string | null | undefined) => !!v && v.trim().length > 0;

/** Город указан: не пустой и не «Не указан». */
export const hasCity = (v: string | null | undefined) =>
  hasText(v) && v!.trim() !== "Не указан" && v!.trim() !== "Другой город" && v!.trim().length >= 2;

/** Нужен хотя бы один валидный контакт; заполненное поле должно быть валидным. */
export function contactIsValid(telegram: string, whatsapp: string): boolean {
  const tg = telegram.trim();
  const wa = whatsapp.trim();
  if (tg && !isTelegramLike(tg)) return false;
  if (wa && !normalizeWhatsapp(wa)) return false;
  return !!tg || !!wa;
}

/** Шаг «Город» выполнен: указан реальный город или отмечено «Я турист». */
export const cityStepDone = (f: Pick<OnboardingFacts, "city" | "tourist">) =>
  hasCity(f.city) || !!f.tourist;

/** visitor_kind при сохранении шага «Город»: галочка туриста главнее, иначе реальный город → resident. */
export function visitorKindForCity(tourist: boolean, current: string | null | undefined): string {
  if (tourist) return "tourist";
  return current === "tourist" ? "resident" : current || "resident";
}

export const ONBOARDING_STEPS: StepDef[] = [
  {
    id: "interests",
    order: 10,
    kind: "required",
    applies: () => true,
    done: (f) => f.interests.length >= f.minInterests,
  },
  {
    id: "city",
    order: 15,
    kind: "required",
    applies: (f) => !cityStepDone(f),
    done: (f) => cityStepDone(f),
  },
  {
    id: "photo",
    order: 20,
    kind: "optional",
    applies: () => true,
    done: (f) => hasText(f.photoUrl),
  },
  {
    id: "about",
    order: 30,
    kind: "required",
    applies: () => true,
    done: (f) => hasText(f.profession),
  },
  {
    id: "meet",
    order: 40,
    kind: "required",
    applies: () => true,
    done: (f) => f.meetStatus.length > 0 || f.hasContact,
  },
  {
    id: "contact",
    order: 50,
    kind: "required",
    applies: (f) => !f.optOut,
    done: (f) => f.hasContact,
  },
  {
    id: "district",
    order: 60,
    kind: "optional",
    // Видимость зависит от текущего города и ответа meet — считается в visibleSteps.
    applies: () => true,
    done: (f) => hasText(f.district) || f.savedStep >= 60,
  },
  { id: "services", order: 70, kind: "optional", applies: () => true, done: (f) => f.isExpert },
];

/** Шаги, которые скрываются при «Пока только изучаю». */
export const OPT_OUT_HIDDEN: StepId[] = ["contact", "district"];

/**
 * Видимые шаги по текущим ответам: при «Пока только изучаю» скрыты контакт и район,
 * район скрыт и тогда, когда для выбранного города нет списка районов.
 */
export function visibleSteps(
  all: StepDef[],
  opts: { optOut: boolean; hasDistrictList: boolean; tourist?: boolean },
): StepDef[] {
  return all.filter((s) => {
    if (opts.optOut && OPT_OUT_HIDDEN.includes(s.id)) return false;
    if (opts.tourist && s.id === "district") return false;
    if (s.id === "district" && !opts.hasDistrictList) return false;
    return true;
  });
}

export type WizardMode = { strict: boolean; restart: boolean };

/**
 * Какие шаги показать.
 * Мягкий режим (без restart): пропускаем всё, что уже заполнено.
 * Строгий режим и restart: показываем все применимые шаги.
 */
export function selectSteps(
  f: OnboardingFacts,
  mode: WizardMode,
  steps: StepDef[] = ONBOARDING_STEPS,
): StepDef[] {
  return steps.filter((s) => {
    if (!s.applies(f)) return false;
    if (!mode.strict && !mode.restart && s.done(f)) return false;
    return true;
  });
}

/** С какого шага продолжить: первый незаполненный обязательный или следующий после onboarding_step. */
export function resumeIndex(list: StepDef[], f: OnboardingFacts, mode: WizardMode): number {
  if (mode.restart) return 0;
  const i = list.findIndex((s) => (s.kind === "required" && !s.done(f)) || s.order > f.savedStep);
  return i === -1 ? list.length : i;
}

const REQUIRED_FOR_EXISTING: StepId[] = ["interests", "city", "about", "meet", "contact"];

/** Остались ли незаполненные обязательные шаги (интересы, город, профессия, знакомства, контакт). */
export function hasPendingRequiredSteps(f: OnboardingFacts): boolean {
  return selectSteps(f, { strict: false, restart: false }).some((s) =>
    REQUIRED_FOR_EXISTING.includes(s.id),
  );
}

/**
 * Мини-мастер ARENA: интересы → о себе и чем занимаешься → (фото) → кого и что ищешь (текст) → контакт.
 * Город, район, услуги и «Пока только изучаю» не показываются.
 */
export const ARENA_STEPS: StepDef[] = [
  {
    id: "interests",
    order: 10,
    kind: "required",
    applies: () => true,
    done: (f) => f.interests.length >= f.minInterests,
  },
  {
    id: "about",
    order: 30,
    kind: "required",
    applies: () => true,
    done: (f) => hasText(f.profession) && hasText(f.about),
  },
  {
    id: "photo",
    order: 20,
    kind: "optional",
    applies: () => true,
    done: (f) => hasText(f.photoUrl),
  },
  /** Свободный текст «Кого и что ищешь на ARENA?» — главный сигнал для участников. */
  {
    id: "seeking",
    order: 40,
    kind: "required",
    applies: () => true,
    done: (f) => hasSeekingText(f.lookingFor),
  },
  { id: "contact", order: 50, kind: "required", applies: () => true, done: (f) => f.hasContact },
];

/**
 * Шаги ARENA только по реальным данным профиля (onboarding_completed_at не учитывается).
 * Фото предлагаем, только если и так есть что заполнить — заполненный профиль идёт сразу в раздел.
 */
export function arenaSteps(f: OnboardingFacts): StepDef[] {
  const pending = ARENA_STEPS.filter((s) => !s.done(f));
  if (!pending.some((s) => s.kind === "required")) return [];
  return pending;
}

/**
 * Новый короткий мастер при регистрации: намерения → город → интересы.
 * Заполненные шаги пропускаются (и у существующих пользователей).
 */
export const WIZARD_STEPS: StepDef[] = [
  { id: "intents", order: 5, kind: "required", applies: () => true, done: (f) => (f.intents?.length ?? 0) > 0 },
  { id: "city", order: 7, kind: "required", applies: () => true, done: (f) => cityStepDone(f) },
  {
    id: "interests",
    order: 10,
    kind: "required",
    applies: () => true,
    done: (f) => f.interests.length >= f.minInterests,
  },
];

/** Шаги нового мастера: при restart — все, иначе только незаполненные. */
export function wizardSteps(f: OnboardingFacts, restart = false): StepDef[] {
  return restart ? WIZARD_STEPS : WIZARD_STEPS.filter((s) => !s.done(f));
}

/** Мини-мастер «Знакомство»: фото (необязательно), чем занимаешься, готовность к знакомствам, контакт. */
export const MEET_STEP_IDS: StepId[] = ["photo", "about", "meet", "contact"];

export function meetSteps(f: OnboardingFacts): StepDef[] {
  const list = ONBOARDING_STEPS.filter((s) => MEET_STEP_IDS.includes(s.id));
  const pending = list.filter((s) => !s.done(f) && !(f.optOut && s.id === "contact"));
  if (!pending.some((s) => s.kind === "required")) return [];
  return pending;
}

/** Профиль для знакомств заполнен (фото и город не требуются). */
export function meetProfileReady(f: Pick<OnboardingFacts, "profession" | "meetStatus" | "hasContact" | "optOut">): boolean {
  const about = hasText(f.profession);
  const meet = f.meetStatus.length > 0 || f.hasContact;
  return about && meet && (f.optOut || f.hasContact);
}
